/**
 * First-run how-to-play intro state for the Warmup hub.
 *
 * Preloads the per-mode "seen" map once on mount so the open decision is instant. `maybeShowIntro(mode)`
 * opens that mode's intro only if unseen; `dismissIntro()` closes it and persists the flag (first-time-only,
 * no opt-out checkbox). A tap made before the map finishes loading is queued and resolved once it arrives,
 * so a first-timer never misses the intro and a returning player never sees a flash.
 *
 * See specs/warmup-game-intros/spec.md.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  loadSeenWarmupIntros,
  markWarmupIntroSeen,
} from '../helpers/warmupIntroSeenStorage.helper';
import type { WarmupIntroMode } from '../constants/warmupIntros.constants';

type SeenMap = Record<WarmupIntroMode, boolean>;

interface UseWarmupIntro {
  /** The mode whose intro is currently open, or null. */
  introFor: WarmupIntroMode | null;
  /** Request the intro for a mode; opens it only if that mode is unseen. Safe to call before preload. */
  maybeShowIntro: (mode: WarmupIntroMode) => void;
  /** Close the open intro and mark it seen (Play or backdrop both route here). */
  dismissIntro: () => void;
}

export function useWarmupIntro(): UseWarmupIntro {
  const [seen, setSeen] = useState<SeenMap | null>(null);
  const [introFor, setIntroFor] = useState<WarmupIntroMode | null>(null);
  const pendingRef = useRef<WarmupIntroMode | null>(null);

  // Preload the seen map once.
  useEffect(() => {
    let active = true;
    loadSeenWarmupIntros().then((map) => {
      if (active) setSeen(map);
    });
    return () => {
      active = false;
    };
  }, []);

  // Resolve a tap that happened while the map was still loading.
  useEffect(() => {
    if (seen === null) return;
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (pending && !seen[pending]) setIntroFor(pending);
  }, [seen]);

  const maybeShowIntro = useCallback(
    (mode: WarmupIntroMode) => {
      if (seen === null) {
        pendingRef.current = mode;
        return;
      }
      if (!seen[mode]) setIntroFor(mode);
    },
    [seen],
  );

  const dismissIntro = useCallback(() => {
    if (!introFor) return;
    markWarmupIntroSeen(introFor);
    setSeen((prev) => ({ ...(prev ?? ({} as SeenMap)), [introFor]: true }));
    setIntroFor(null);
  }, [introFor]);

  return { introFor, maybeShowIntro, dismissIntro };
}
